<template>
	<div
		ref="multiFormatPlayer"
		class="multi-format-player"
		:class="[`is-${uiStatus}`, { 'is-mobile-portrait': internalIsMobilePortrait }]"
	>
		<div class="multi-format-player__media-w">
			<div class="multi-format-player__media-w-inner js-multi-format-tool-player">
				<div
					ref="videoContainer"
					class="multi-format-player__video-container"
				>
					<slot name="video-overlay" />
					<video
						ref="videoPlayer"
						class="multi-format-player__video-player"
						playsinline
						preload="metadata"
					/>
				</div>
				<div
					v-show="uiStatus !== 'preview'"
					ref="cropContainer"
					class="multi-format-player__crop-container"
				>
					<canvas
						ref="cropCanvas"
						class="multi-format-player__crop-canvas"
					/>
				</div>
			</div>
		</div>
		<PlayerControls
			ref="playerControls"
			class="multi-format-player__controls"
			:class="{ 'is-hidden': !showControls }"
			:duration="playerControlsDuration"
			:position="playerControlsPosition"
			:is-playing="isPlaying"
			:show-scrubber="true"
			:is-muted="isMuted"
			:volume-level="volumeLevel"
			:show-fullscreen="false"
			:show-share-button="false"
			:timeline-full-width="true"
			:controls-full-width="!internalIsMobilePortrait"
			:show-volume-selector="!internalIsMobilePortrait"
			:dark-theme="$arenaConfig?.isLightTheme && isCommunityCropping"
			@play="togglePlay(true)"
			@pause="togglePlay(false)"
			@set-mute="setMute($event)"
			@set-volume="setVolume($event)"
			@seek="setVideoTime($event)"
		/>
	</div>
</template>

<script>
import Hls from 'hls.js';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import PlayerControls from '@shared/components/PlayerControls/PlayerControls.vue';

const CROPPER_SETTINGS = {
	background:       false,
	modal:            false,
	center:           false,
	guides:           false,
	cropBoxResizable: false,
	aspectRatio:      9 / 16,
	autoCropArea:     1,
	viewMode:         0,
	dragMode:         'move',
	responsive:       true,
	zoomable:         false,
	scalable:         false,
};

const urlParams = new URLSearchParams(window.location.search);
const requestedPosition = urlParams.get('position');

export default {
	name:       'MultiFormatPlayer',
	components: {
		PlayerControls,
	},
	props: {
		hlsUrl: {
			type:    String,
			default: null,
		},
		uiStatus: {
			type:     String,
			required: true,
		},
		showControls: {
			type:    Boolean,
			default: true,
		},
		tagTime: {
			type:    Object,
			default: null,
		},
		internalIsMobilePortrait: {
			type:    Boolean,
			default: false,
		},
		streamLeadTimeSeconds: {
			type:    Number,
			default: 0,
		},
		isCommunityCropping: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			duration:       0,
			position:       requestedPosition ? parseFloat(requestedPosition) : 0,
			isPlaying:      false,
			isMuted:        false,
			volumeLevel:    50,
			containerWidth: 1200,
			cropper:        null,
		};
	},
	computed: {
		playerControlsDuration() {
			return this.tagTime ? this.tagTime.end - this.tagTime.start : this.duration - this.streamLeadTimeSeconds;
		},
		playerControlsPosition() {
			return this.tagTime ? this.position - this.tagTime.start : this.position - this.streamLeadTimeSeconds;
		},
	},
	watch: {
		playerControlsDuration() {
			this.$emit('duration-update', this.playerControlsDuration);
		},
	},
	mounted() {
		if (this.hlsUrl && Hls.isSupported()) {
			const hls = new Hls();
			hls.loadSource(this.hlsUrl);
			hls.attachMedia(this.$refs.videoPlayer);
		} else if (this.$refs.videoPlayer.canPlayType('application/vnd.apple.mpegurl')) {
			this.$refs.videoPlayer.src = this.hlsUrl; // safari fix
		}

		this.$refs.videoPlayer.addEventListener('loadedmetadata', this.handleLoadedMetadata);
		this.$refs.videoPlayer.addEventListener('timeupdate', this.handleTimeUpdate);
		this.$refs.videoPlayer.addEventListener('durationchange', this.handleDurationChange);

		this.$refs.videoPlayer.removeAttribute('controls');

		this.initializeCropper();

		this.$refs.cropCanvas.addEventListener('ready', this.handleCropperReady);
		this.$refs.cropCanvas.addEventListener('cropstart', this.handleCropStart);
	},
	beforeDestroy() {
		// Clean up event listeners to prevent memory leaks
		this.$refs.videoPlayer.removeEventListener('loadedmetadata', this.handleLoadedMetadata);
		this.$refs.videoPlayer.removeEventListener('timeupdate', this.handleTimeUpdate);
		this.$refs.videoPlayer.removeEventListener('durationchange', this.handleDurationChange);
		this.$refs.cropCanvas.removeEventListener('ready', this.handleCropperReady);
		this.$refs.cropCanvas.removeEventListener('cropstart', this.handleCropStart);

		// Destroy cropper instance
		if (this.cropper) {
			this.cropper.destroy();
		}
	},
	methods: {
		initializeCropper() {
			this.cropper = new Cropper(this.$refs.cropCanvas, {
				...CROPPER_SETTINGS,
				minCropBoxHeight: this.containerWidth * (9 / 16) + 1,
				cropmove:         this.handleCropMove,
			});
		},
		handleCropMove() {
			this.$emit('coordinates-update', this.cropper.getCropBoxData());
		},
		handleDurationChange() {
			this.duration = this.$refs.videoPlayer.duration;
		},
		async handleTimeUpdate(event) {
			this.position = event.target?.currentTime;

			// stopping 0.1s before the end to prevent some glitches
			if ((this.tagTime && this.position + 0.1 >= this.tagTime.end) || (this.position + 0.1 >= this.duration)) {
				await this.togglePlay(false);
				this.$emit('video-ended');
			}
		},
		async togglePlay(value) {
			if (value) {
				await this.$refs.videoPlayer.play();
			} else {
				this.$refs.videoPlayer.pause();
			}
			this.isPlaying = value;
		},
		pause() {
			this.$refs.videoPlayer.pause();
			this.isPlaying = false;
		},
		async play() {
			await this.$refs.videoPlayer.play();
			this.isPlaying = true;
		},
		async setVideoTime(time, streamLeadTimeIncluded = false) {
			if (!streamLeadTimeIncluded) {
				time += this.streamLeadTimeSeconds;
			}
			if (this.tagTime) {
				time += this.tagTime.start;
			}
			this.position = time;
			this.$refs.videoPlayer.currentTime = time;
		},
		seek(time) {
			if (this.playerControlsPosition + time < 0) {
				this.setVideoTime(0);
			} else if (this.playerControlsPosition + time > this.playerControlsDuration) {
				this.setVideoTime(this.playerControlsDuration);
			} else {
				this.setVideoTime(this.playerControlsPosition + time);
			}
		},
		getPlayerData() {
			this.duration = this.$refs.videoPlayer.duration;
			this.isPlaying = !this.$refs.videoPlayer.paused;
			this.isMuted = this.$refs.videoPlayer.muted;
			this.volumeLevel = this.$refs.videoPlayer.volume * 100;
		},
		setMute(value) {
			if (value && this.volumeLevel === 0) {
				this.setVolume(50);
			}
			this.$refs.videoPlayer.muted = value;
			this.isMuted = value;
		},
		setVolume(value) {
			this.$refs.videoPlayer.volume = value / 100;
			this.volumeLevel = value;
			if (value > 0 && this.isMuted) {
				this.setMute(false);
			} else if (value === 0) {
				this.setMute(true);
			}
		},
		handleLoadedMetadata() {
			this.getPlayerData();
			if (requestedPosition) {
				this.$refs.videoPlayer.currentTime = parseFloat(parseFloat(requestedPosition) + this.streamLeadTimeSeconds);
			} else {
				this.$refs.videoPlayer.currentTime = this.tagTime ? this.tagTime.start : (0 + this.streamLeadTimeSeconds);
			}
		},
		handleCropperReady() {
			this.$emit('coordinates-update', this.cropper.getCropBoxData());
		},
		handleCropStart() {
			this.$emit('cropstart');
		},
		getCropContainerSize() {
			return {
				width:  this.$refs.cropContainer.clientWidth,
				height: this.$refs.cropContainer.clientHeight,
			};
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.multi-format-player {
	position: relative;
}

.multi-format-player__media-w {
	background-color: var(--color-black);
}

.multi-format-player__media-w-inner {
	position: relative;
	max-height: 400px;
	aspect-ratio: 16 / 9;
	margin: 0 auto;
}

.multi-format-player__video-container {
	overflow-x: clip;
	overflow-y: visible;
	position: relative;
	width: 100%;
	aspect-ratio: 16 / 9;

	.multi-format-player.is-ready &,
	.multi-format-player.is-recording & {
		&::after {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			pointer-events: none;
			z-index: 1;
			border: 1px solid var(--color-hype-yellow);
		}
	}

	.multi-format-player.is-recording & {
		&::after {
			border-color: var(--color-red);
		}
	}
}

.multi-format-player__video-player {
	top: 0;
	left: 0;
	width: 100%;
	height: auto;
	position: absolute;
}

.multi-format-player__controls {
	&.is-hidden {
		visibility: hidden;
		pointer-events: none;
		opacity: 0;
	}
}

.multi-format-player__crop-container {
	position: absolute;
	top: 0;
	left: 0;
	overflow-x: clip;
	overflow-y: visible;
	width: 100%;
	aspect-ratio: 16 / 9;
}

.multi-format-player__crop-canvas {
	display: block;
	width: 100%;
	aspect-ratio: 16 / 9;
}

::v-deep {
	.cropper-container {
		overflow: hidden;
	}

	.cropper-face {
		background-color: transparent;
	}

	.cropper-wrap-box {
		pointer-events: none;
	}

	.cropper-canvas,
	.cropper-drag-box {
		pointer-events: none;
		cursor: default;
	}

	.cropper-view-box {
		outline: none;
		border: none;
	}

	.cropper-crop-box {
		box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.75); // stylelint-disable-line
		will-change: transform;
	}
}

</style>
